import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { useWorkflow } from "../../workflow/WorkflowContext";
import { FilingStatusBar } from "../../components/FilingStatusBar";
import { RegimesApplied } from "../../components/RegimesApplied";
import { CountryReturnsInline } from "../../components/CountryReturnsInline";
import { FilingSections } from "../../components/FilingSections";
import { ViesHeldQueue } from "../../components/ViesHeldQueue";
import { isSignedOff } from "../../workflow/calcStatus";
import { GroupedReviewQueue } from "../../components/GroupedReviewQueue";
import { SavedAnswersBar } from "../../components/review_queue/SavedAnswersBar";
import { ChannelContinuityNote } from "../../components/ChannelContinuityNote";
import { FiledPeriodNote } from "../../components/FiledPeriodNote";
import { ThresholdTimeline } from "../../components/ThresholdTimeline";
import { NoActiveCalcNotice } from "../../components/NoActiveCalcNotice";

/** Step 4 — Review. v0.6 nav refactor: dedicated route component. The
 *  Inspect/drill handler is provided by WorkflowLayout via <Outlet
 *  context={{ onDrill }} />. */
export default function Step4Review() {
  const { onDrill } = useOutletContext() || {};
  const { calc, state, setActiveStep, countryFilter, review, clients, clientId } = useWorkflow();
  const { t } = useTranslation();
  const [highlightSection, setHighlightSection] = useState(null);
  const cf = (countryFilter || "ALL").toUpperCase();
  // Once the seller has an OSS registration the €10,000 distance-sales
  // threshold meter is no longer relevant — hide it: registering is the
  // Art 59c(3) election to tax at destination, and the calculation already
  // treats the client that way. Operator check 2026-10-16 (A2-391): this
  // read calc.seller_registrations_used, which holds country codes only, so
  // it never matched and the meter said "No cross-border B2C transactions"
  // for a client that had them. Read the client's own registrations.
  const client = (clients || []).find((c) => c.id === (calc?.client_id || clientId));
  const hasOssRegistration = (client?.company_vat_numbers || []).some((r) => {
    const rt = (r.registration_type || r.type || "").toUpperCase();
    return rt === "OSS_UNION" || rt === "OSS_NON_UNION" || rt === "OSS";
  });
  if (!calc) return <NoActiveCalcNotice onGoToUpload={() => setActiveStep(1)} />;
  return (
    <section data-testid="step-4-review" className="space-y-4">
      <FilingStatusBar calcId={calc?.id} />
      <SavedAnswersBar calcId={calc?.id} refreshKey={review}
        locked={!!calc?.frozen} />
      {cf !== "ALL" && (
        <div data-testid="country-filter-active-chip" className="border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/40 px-4 py-2 tabular-nums text-xs text-teal-700 dark:text-teal-300 flex items-center justify-between">
          <span>
            <span className="opacity-70">{t("ui.dashboard.filter_active_label")}</span>{" "}
            <span className="font-bold">{cf}</span>
            <span className="opacity-60 ml-3">{t("ui.dashboard.filter_active_hint", { cf })}</span>
          </span>
          <button
            type="button"
            data-testid="country-filter-clear"
            onClick={() => setActiveStep(1)}
            className="text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300 text-[10px] tracking-normal underline decoration-dotted"
          >
            {t("ui.review_queue.change_filter", { defaultValue: "Change filter ↑" })}
          </button>
        </div>
      )}
      <RegimesApplied regimes={calc?.regimes_applied} registrations={calc?.seller_registrations_used} />
      <CountryReturnsInline calcId={calc?.id} />
      <FilingSections
        rows={calc?.template_summary}
        countryFilter={countryFilter}
        sectionFlagTotals={review?.section_totals || {}}
        onFlagBadgeClick={(id) => {
          setHighlightSection(id);
          document
            .querySelector('[data-testid="grouped-review-queue"]')
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />
      {/* the VAT-number decision route refuses after sign-off too
          (api/_signoff_guard), unlike a re-run, which the saved-answers
          bar above still offers on a calc only signed */}
      <ViesHeldQueue calcId={calc?.id}
        locked={!!calc?.frozen || isSignedOff(calc) || !!calc?.superseded_by_calc_id}
        lockedBy={calc?.frozen ? "frozen" : calc?.superseded_by_calc_id && !isSignedOff(calc) ? "replaced" : "signed"} />
      <ChannelContinuityNote calcId={calc?.id} />
      <FiledPeriodNote calcId={calc?.id} />
      <GroupedReviewQueue calcId={calc?.id} highlightSection={highlightSection} onInspect={onDrill} />
      {!hasOssRegistration && (
        <ThresholdTimeline
          steps={calc?.threshold_fold}
          applicable={calc?.distance_threshold_applies !== false}
        />
      )}
      <button type="button" data-testid="step-4-continue"
        disabled={!state.steps[4].complete}
        title={!state.steps[4].complete ? state.steps[4].blockers.join(" · ") : ""}
        onClick={() => setActiveStep(5)}
        className="bg-teal-600 hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 tabular-nums text-xs rounded-xl">
        {t("ui.dashboard.continue_to_step", { idx: 5, label: t("ui.stepper.step5") })}
      </button>
    </section>
  );
}
